import React from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper
} from '@mui/material';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import LuggageIcon from '@mui/icons-material/Luggage';
import LanguageIcon from '@mui/icons-material/Language';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import WifiIcon from '@mui/icons-material/Wifi';
import RestaurantIcon from '@mui/icons-material/Restaurant';

const TravelTipsPage = () => {
  const travelTips = [
    {
      category: 'Planning & Preparation',
      tips: [
        { 
          title: 'Book flights 2-3 months in advance', 
          description: 'For the best deals, book international flights 2-3 months ahead and domestic flights 1-2 months ahead.',
          icon: <FlightTakeoffIcon color="primary" />
        },
        { 
          title: 'Make a packing checklist', 
          description: 'Create a comprehensive checklist organized by categories like clothing, toiletries, electronics, and documents.',
          icon: <LuggageIcon color="primary" />
        },
        { 
          title: 'Learn basic local phrases', 
          description: 'Knowing simple greetings, "please," "thank you," and "excuse me" in the local language goes a long way.',
          icon: <LanguageIcon color="primary" />
        }
      ]
    },
    {
      category: 'Money & Budgeting',
      tips: [
        { 
          title: 'Notify your bank about travel plans', 
          description: 'Avoid having your cards blocked for suspicious activity by informing your bank about your travel dates and destinations.',
          icon: <AttachMoneyIcon color="primary" />
        },
        { 
          title: 'Carry multiple payment methods', 
          description: 'Have a mix of cash, credit cards, and debit cards in case one payment method is not accepted.',
          icon: <AttachMoneyIcon color="primary" />
        }
      ]
    },
    {
      category: 'Health & Safety',
      tips: [
        { 
          title: 'Pack a basic first-aid kit', 
          description: 'Include band-aids, pain relievers, anti-diarrheal medication, antihistamines, and any prescription medications.',
          icon: <HealthAndSafetyIcon color="primary" />
        },
        { 
          title: 'Keep digital copies of important documents', 
          description: 'Store copies of your passport, insurance, and other important documents in cloud storage or email them to yourself.',
          icon: <HealthAndSafetyIcon color="primary" />
        }
      ]
    },
    {
      category: 'Technology & Photography',
      tips: [
        { 
          title: 'Bring universal power adapters', 
          description: 'Research the outlet types at your destination and bring appropriate adapters for all your devices.',
          icon: <WifiIcon color="primary" />
        },
        { 
          title: 'Download offline maps', 
          description: 'Save offline maps of your destination on Google Maps or other map apps for navigation without internet.',
          icon: <WifiIcon color="primary" />
        },
        { 
          title: 'Protect your camera equipment', 
          description: 'Keep cameras in protective cases and avoid changing lenses in dusty or humid environments.',
          icon: <PhotoCameraIcon color="primary" />
        }
      ]
    },
    {
      category: 'Food & Culture',
      tips: [
        { 
          title: 'Research local customs and etiquette', 
          description: 'Learn about local cultural norms to avoid unintentionally disrespectful behavior.',
          icon: <RestaurantIcon color="primary" />
        },
        { 
          title: 'Try street food (carefully)', 
          description: 'Street food offers authentic local flavors. Look for stands with long lines of locals and visible food preparation.',
          icon: <RestaurantIcon color="primary" />
        }
      ]
    }
  ];

  return (
    <Box sx={{ pt: 8, pb: 8 }}>
      <Box 
        sx={{ 
          bgcolor: 'primary.main', 
          color: 'white', 
          py: 6,
          mb: 6
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
            Travel Tips & Advice
          </Typography>
          <Typography variant="h6">
            Make your journey smoother, safer, and more enjoyable with our expert travel tips
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg">
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Card 
              elevation={3} 
              sx={{ 
                height: '100%', 
                transition: 'transform 0.3s ease-in-out', 
                '&:hover': { transform: 'translateY(-5px)' } 
              }}
            >
              <CardMedia
                component="img"
                height="200"
                image="https://images.unsplash.com/photo-1498307833015-e7b400441eb8"
                alt="Packing for travel"
              />
              <CardContent>
                <Typography gutterBottom variant="h5" component="div">
                  Before You Go
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Essential preparation tips, from booking accommodations to packing efficiently and securing necessary documents.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card 
              elevation={3} 
              sx={{ 
                height: '100%', 
                transition: 'transform 0.3s ease-in-out', 
                '&:hover': { transform: 'translateY(-5px)' } 
              }}
            >
              <CardMedia
                component="img"
                height="200"
                image="https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1"
                alt="During travel"
              />
              <CardContent>
                <Typography gutterBottom variant="h5" component="div">
                  During Your Trip
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tips for navigating transportation, staying healthy, experiencing local culture, and capturing memories.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card 
              elevation={3} 
              sx={{ 
                height: '100%', 
                transition: 'transform 0.3s ease-in-out', 
                '&:hover': { transform: 'translateY(-5px)' } 
              }}
            >
              <CardMedia
                component="img"
                height="200"
                image="https://images.unsplash.com/photo-1539635278303-d4002c07eae3"
                alt="After travel"
              />
              <CardContent>
                <Typography gutterBottom variant="h5" component="div">
                  Travel Smart
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Money-saving strategies, safety advice, and tech tips to make your journey smoother and more enjoyable.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ mt: 8 }}>
          <Typography variant="h4" component="h2" gutterBottom fontWeight="bold">
            Expert Travel Tips
          </Typography>
          <Typography variant="body1" paragraph>
            Whether you're a first-time traveler or a seasoned globetrotter, these tips will help you have a safer, more enjoyable journey.
          </Typography>
          
          {travelTips.map((category, index) => (
            <Paper elevation={2} sx={{ p: 3, mt: 4 }} key={index}>
              <Typography variant="h5" gutterBottom color="primary.main" fontWeight="bold">
                {category.category}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <List>
                {category.tips.map((tip, tipIndex) => (
                  <ListItem key={tipIndex} alignItems="flex-start" sx={{ py: 2 }}>
                    <ListItemIcon>
                      {tip.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={<Typography variant="h6">{tip.title}</Typography>}
                      secondary={tip.description}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default TravelTipsPage; 